import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Typography,
  TextField,
  FormControl,
  Checkbox,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithSessionHandling } from '../utils/authFetch';
import PasswordTextField from './PasswordTextField';

interface User {
  id: string;
  version: number;
  email: string;
  username: string;
  hospitalName?: string;
  isAdmin: boolean;
  isVip: boolean;
  isTrial: boolean;
  isExpired: boolean;
  isViewer: boolean;
  disabled: boolean;
  allowVerificationSkip: boolean;
  hasActiveSubscription: boolean;
  hasBankTransferSubscription: boolean;
  isDefaultAdmin?: boolean;
  /** 追加前の一時ユーザーかどうか */
  isNew?: boolean;
  /** 保存時に削除するかどうか */
  toDelete?: boolean;
  /** 新規ユーザー用のパスワード */
  password?: string;
}

type UserRoleValue = 'none' | 'vip' | 'trial' | 'admin' | 'viewer' | 'expired';
type SortKey = 'role' | 'disabled' | 'allowVerificationSkip';
type SortOrder = 'asc' | 'desc';
type SortConfig = {
  key: SortKey;
  direction: SortOrder;
};

interface NewUser {
  email: string;
  username: string;
  hospitalName: string;
  password: string;
  role: Exclude<UserRoleValue, 'viewer'>;
  disabled: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const TABLE_ROLE_OPTIONS: ReadonlyArray<{
  value: UserRoleValue;
  label: string;
  disabled?: boolean;
}> = [
  { value: 'none', label: '通常' },
  { value: 'vip', label: 'VIP' },
  { value: 'trial', label: '試用' },
  { value: 'admin', label: '管理者' },
  { value: 'viewer', label: '閲覧用', disabled: true },
  { value: 'expired', label: '期限切れ' },
];

const ROLE_SORT_ORDER: Record<UserRoleValue, number> = {
  admin: 0,
  vip: 1,
  trial: 2,
  none: 3,
  expired: 4,
  viewer: 5,
};

const isLockedUser = (user: User) => Boolean(user.isDefaultAdmin);
const isVerificationSkipDisabled = (user: User) =>
  isLockedUser(user) ||
  user.toDelete ||
  user.isViewer ||
  user.isAdmin ||
  user.isVip ||
  user.isExpired ||
  (user.hasActiveSubscription && !user.hasBankTransferSubscription);

const getUserRoleValue = (user: User): UserRoleValue => {
  if (user.isViewer) return 'viewer';
  if (user.isAdmin) return 'admin';
  if (user.isVip) return 'vip';
  if (user.isTrial) return 'trial';
  if (user.isExpired) return 'expired';
  return 'none';
};
const createRequestId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;


const DbAdmin: React.FC = () => {
  const { token, logout } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [originalUsers, setOriginalUsers] = useState<User[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'role',
    direction: 'asc',
  });
  const [newUser, setNewUser] = useState<NewUser>({
    email: '',
    username: '',
    hospitalName: '',
    password: '',
    role: 'none',
    disabled: false,
  });
  const saveInFlightRef = useRef(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasConflict, setHasConflict] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const normalizeUsers = (data: User[]): User[] =>
    data.map((u) => ({
      ...u,
      hospitalName: u.hospitalName || '',
      isTrial: u.isTrial ?? false,
      isExpired: u.isExpired ?? false,
      isViewer: u.isViewer ?? false,
      allowVerificationSkip: u.allowVerificationSkip ?? false,
      hasActiveSubscription: u.hasActiveSubscription ?? false,
      hasBankTransferSubscription: u.hasBankTransferSubscription ?? false,
    }));

  const fetchUsers = async () => {
    const authToken = token;
    const { response, sessionExpired } = await fetchWithSessionHandling(
      `${API_BASE_URL}/api/admin/db/users`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      },
      { logout },
    );
    if (sessionExpired) {
      return;
    }
    if (response.ok) {
      const normalized = normalizeUsers(await response.json());
      setUsers(normalized);
      setOriginalUsers(normalized);
      setHasConflict(false);
    }
  };

  const handleRoleChange = (
    id: string,
    role: 'none' | 'vip' | 'trial' | 'admin' | 'expired',
  ) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === id
          ? isLockedUser(u)
            ? u
            : {
                ...u,
                isAdmin: role === 'admin',
                isVip: role === 'vip',
                isTrial: role === 'trial',
                isExpired: role === 'expired',
              }
          : u,
      ),
    );
  };

  const handleDisabledChange = (id: string, disabled: boolean) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === id
          ? isLockedUser(u)
            ? u
            : { ...u, disabled }
          : u,
      ),
    );
  };

  const handleAllowVerificationSkipChange = (
    id: string,
    allowVerificationSkip: boolean,
  ) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === id
          ? isLockedUser(u)
            ? u
            : { ...u, allowVerificationSkip }
          : u,
      ),
    );
  };

  const saveChanges = async () => {
    if (saveInFlightRef.current) return;
    const authToken = token;

    const toAdd = users.filter((u) => u.isNew && !u.toDelete);
    const toDelete = users.filter((u) => u.toDelete && !u.isNew);
    const changed = users.filter((u) => {
      if (u.isNew || u.toDelete || isLockedUser(u)) return false;
      const orig = originalUsers.find((o) => o.id === u.id);
      return (
        orig &&
        (orig.isAdmin !== u.isAdmin ||
          orig.isVip !== u.isVip ||
          orig.isTrial !== u.isTrial ||
          orig.isExpired !== u.isExpired ||
          orig.disabled !== u.disabled ||
          orig.allowVerificationSkip !== u.allowVerificationSkip ||
          orig.hospitalName !== u.hospitalName)
      );
    });

    if (toAdd.length === 0 && toDelete.length === 0 && changed.length === 0) {
      setSnackbar({ open: true, message: '変更はありません', severity: 'success' });
      return;
    }

    const operations = [
      ...toAdd.map((u) => ({
        action: 'create',
        clientId: u.id,
        data: {
          email: u.email,
          username: u.username,
          hospitalName: u.hospitalName,
          password: u.password!,
          is_admin: u.isAdmin,
          is_vip: u.isVip,
          isTrial: u.isTrial,
          isExpired: u.isExpired,
          disabled: u.disabled,
        },
      })),
      ...toDelete.map((u) => ({
        action: 'delete',
        userId: u.id,
        expectedVersion: u.version,
        data: {},
      })),
      ...changed.map((u) => ({
        action: 'update',
        userId: u.id,
        expectedVersion: u.version,
        data: {
          is_admin: u.isAdmin,
          is_vip: u.isVip,
          isTrial: u.isTrial,
          isExpired: u.isExpired,
          disabled: u.disabled,
          hospitalName: u.hospitalName,
          allowVerificationSkip: u.allowVerificationSkip,
        },
      })),
    ];

    setIsSaving(true);
    saveInFlightRef.current = true;
    setHasConflict(false);
    try {
      const { response, sessionExpired } = await fetchWithSessionHandling(
        `${API_BASE_URL}/api/admin/db/users/batch`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ requestId: createRequestId(), operations }),
        },
        { logout },
      );
      if (sessionExpired) return;
      if (!response.ok) {
        const isConflict = response.status === 409;
        setHasConflict(isConflict);
        setSnackbar({
          open: true,
          message: isConflict
            ? '\u4ed6\u306e\u7ba1\u7406\u8005\u306b\u3088\u308b\u5909\u66f4\u3068\u7af6\u5408\u3057\u307e\u3057\u305f\u3002\u7de8\u96c6\u5185\u5bb9\u306f\u4fdd\u6301\u3055\u308c\u3066\u3044\u307e\u3059'
            : '\u4fdd\u5b58\u306b\u5931\u6557\u3057\u307e\u3057\u305f\u3002DB\u3078\u306e\u5909\u66f4\u306f\u884c\u308f\u308c\u3066\u3044\u307e\u305b\u3093',
          severity: 'error',
        });
        return;
      }
      const result = await response.json();
      const normalized = normalizeUsers(result.users);
      setUsers(normalized);
      setOriginalUsers(normalized);
      setSnackbar({ open: true, message: '\u4fdd\u5b58\u3057\u307e\u3057\u305f', severity: 'success' });
    } catch {
      setSnackbar({
        open: true,
        message: '\u4fdd\u5b58\u306b\u5931\u6557\u3057\u307e\u3057\u305f\u3002\u7de8\u96c6\u5185\u5bb9\u306f\u4fdd\u6301\u3055\u308c\u3066\u3044\u307e\u3059',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
      saveInFlightRef.current = false;
    }
  };

  const addUser = () => {
    const trimmedEmail = newUser.email.trim();
    const trimmedUsername = newUser.username.trim();
    if (!trimmedEmail || !trimmedUsername || !newUser.password) return;
    const normalizedEmail = trimmedEmail.toLowerCase();
    const isDuplicate = users.some(
      (user) =>
        !user.toDelete && user.email.trim().toLowerCase() === normalizedEmail,
    );
    if (isDuplicate) {
      setSnackbar({
        open: true,
        message: 'メールアドレスが既に登録されています',
        severity: 'error',
      });
      return;
    }
    const tempId = `new-${Date.now()}`;
    const role = newUser.role;
    setUsers((prev) => [
      ...prev,
      {
        id: tempId,
        version: 1,
        email: normalizedEmail,
        username: trimmedUsername,
        hospitalName: newUser.hospitalName.trim(),
        password: newUser.password,
        isAdmin: role === 'admin',
        isVip: role === 'vip',
        isTrial: role === 'trial',
        isExpired: role === 'expired',
        disabled: newUser.disabled,
        isNew: true,
        allowVerificationSkip: false,
        isViewer: false,
        hasActiveSubscription: false,
        hasBankTransferSubscription: false,
      },
    ]);
    setNewUser({
      email: '',
      username: '',
      hospitalName: '',
      password: '',
      role: 'none',
      disabled: false,
    });
    setShowPassword(false);
  };

  const toggleDelete = (id: string) => {
    const target = users.find((u) => u.id === id);
    if (!target || isLockedUser(target)) return;
    if (target.isNew) {
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } else {
      setUsers((prev) =>
        prev.map((u) =>
          u.id === id ? { ...u, toDelete: !u.toDelete } : u,
        ),
      );
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
  };

  const sortedUsers = useMemo(() => {
    const sorted = [...users];
    sorted.sort((a, b) => {
      let aValue: number;
      let bValue: number;

      switch (sortConfig.key) {
        case 'disabled':
          aValue = Number(a.disabled);
          bValue = Number(b.disabled);
          break;
        case 'allowVerificationSkip':
          aValue = Number(a.allowVerificationSkip);
          bValue = Number(b.allowVerificationSkip);
          break;
        case 'role':
        default:
          aValue = ROLE_SORT_ORDER[getUserRoleValue(a)] ?? Number.MAX_SAFE_INTEGER;
          bValue = ROLE_SORT_ORDER[getUserRoleValue(b)] ?? Number.MAX_SAFE_INTEGER;
          break;
      }

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [users, sortConfig]);

  useEffect(() => {
    fetchUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        DB操作
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button variant="contained" onClick={fetchUsers} disabled={isSaving}>
          再読み込み
        </Button>
        <Button variant="contained" color="primary" onClick={saveChanges} disabled={isSaving}>
          保存
        </Button>
        {hasConflict && (
          <Button variant="outlined" color="warning" onClick={fetchUsers} disabled={isSaving}>
            {'\u7af6\u5408\u5185\u5bb9\u3092\u7834\u68c4\u3057\u3066\u518d\u8aad\u307f\u8fbc\u307f'}
          </Button>
        )}
      </Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          ユーザー追加
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <TextField
              label="メール"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              size="small"
              sx={{ flex: '1 1 240px', minWidth: { xs: '100%', sm: 220 } }}
            />
            <TextField
              label="ユーザー名"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              size="small"
              inputProps={{ maxLength: 15 }}
              sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 200 } }}
            />
            <TextField
              label="病院名"
              value={newUser.hospitalName}
              onChange={(e) =>
                setNewUser({ ...newUser, hospitalName: e.target.value })
              }
              size="small"
              inputProps={{ maxLength: 20 }}
              sx={{ flex: '1 1 240px', minWidth: { xs: '100%', sm: 200 } }}
            />
            <PasswordTextField
              label="パスワード"
              type={showPassword ? 'text' : 'password'}
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="パスワードの表示切り替え"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ flex: '1 1 240px', minWidth: { xs: '100%', sm: 220 } }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'flex-end',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={newUser.disabled}
                  onChange={(e) =>
                    setNewUser({ ...newUser, disabled: e.target.checked })
                  }
                />
              }
              label="未認証"
              sx={{
                flex: '0 0 auto',
                alignSelf: 'flex-end',
                minWidth: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'flex-start', sm: 'flex-start' },
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                flex: '1 1 300px',
                minWidth: { xs: '100%', sm: 280 },
              }}
            >
              <FormControl component="fieldset" sx={{ flex: '1 1 auto' }}>
                <RadioGroup
                  row
                  value={newUser.role}
                  onChange={(e) =>
                    setNewUser({ ...newUser, role: e.target.value as NewUser['role'] })
                  }
                  sx={{ flexWrap: 'nowrap' }}
                >
                  <FormControlLabel value="none" control={<Radio />} label="通常" />
                  <FormControlLabel value="vip" control={<Radio />} label="VIP" />
                  <FormControlLabel value="admin" control={<Radio />} label="管理者" />
                  <FormControlLabel
                    value="viewer"
                    control={<Radio />}
                    label="閲覧用"
                    disabled
                  />
                  <FormControlLabel
                    value="trial"
                    control={<Radio />}
                    label="試用"
                  />
                  <FormControlLabel
                    value="expired"
                    control={<Radio />}
                    label="期限切れ"
                  />
                </RadioGroup>
              </FormControl>
              <Button
                variant="contained"
                onClick={addUser}
                sx={{
                  flexShrink: 0,
                  minWidth: { xs: '100%', sm: 96 },
                  alignSelf: { xs: 'stretch', sm: 'center' },
                }}
              >
                追加
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>メール</TableCell>
            <TableCell>病院名</TableCell>
            <TableCell sx={{ minWidth: 140 }}>ユーザー名</TableCell>
            <TableCell
              sortDirection={sortConfig.key === 'role' ? sortConfig.direction : false}
              sx={{ width: 180, minWidth: 160, whiteSpace: 'nowrap' }}
            >
              <TableSortLabel
                sx={{ '& .MuiTableSortLabel-icon': { opacity: 1, color: 'inherit' } }}
                active={sortConfig.key === 'role'}
                direction={sortConfig.key === 'role' ? sortConfig.direction : 'asc'}
                onClick={() => handleSort('role')}
              >
                権限
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortConfig.key === 'disabled' ? sortConfig.direction : false}>
              <TableSortLabel
                sx={{ '& .MuiTableSortLabel-icon': { opacity: 1, color: 'inherit' } }}
                active={sortConfig.key === 'disabled'}
                direction={sortConfig.key === 'disabled' ? sortConfig.direction : 'asc'}
                onClick={() => handleSort('disabled')}
              >
                未認証
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={
                sortConfig.key === 'allowVerificationSkip' ? sortConfig.direction : false
              }
            >
              <TableSortLabel
                sx={{ '& .MuiTableSortLabel-icon': { opacity: 1, color: 'inherit' } }}
                active={sortConfig.key === 'allowVerificationSkip'}
                direction={
                  sortConfig.key === 'allowVerificationSkip' ? sortConfig.direction : 'asc'
                }
                onClick={() => handleSort('allowVerificationSkip')}
              >
                認証無
              </TableSortLabel>
            </TableCell>
            <TableCell align="center">削除</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedUsers.map((u) => {
            const isDefaultAdmin = isLockedUser(u);
            const rowOpacity = isDefaultAdmin ? 0.5 : u.toDelete ? 0.3 : 1;
            return (
              <TableRow key={u.id} sx={{ opacity: rowOpacity }}>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.hospitalName || '-'}</TableCell>
                <TableCell sx={{ minWidth: 140 }}>{u.username}</TableCell>
                <TableCell sx={{ width: 180, minWidth: 160 }}>
                  <FormControl
                    fullWidth
                    size="small"
                    disabled={isSaving || isDefaultAdmin || u.toDelete || u.isViewer}
                  >
                    <Select
                      value={getUserRoleValue(u)}
                      onChange={(e) =>
                        handleRoleChange(
                          u.id,
                          e.target.value as 'none' | 'vip' | 'trial' | 'admin' | 'expired',
                        )
                      }
                      sx={{ minWidth: 0 }}
                    >
                      {TABLE_ROLE_OPTIONS.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Checkbox
                    checked={u.disabled}
                    onChange={(e) => handleDisabledChange(u.id, e.target.checked)}
                    disabled={isSaving || isDefaultAdmin || u.toDelete}
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    checked={u.allowVerificationSkip}
                    onChange={(e) =>
                      handleAllowVerificationSkipChange(u.id, e.target.checked)
                    }
                    disabled={isSaving || isVerificationSkipDisabled(u)}
                  />
                </TableCell>
                <TableCell align="center">
                  <Button
                    color="error"
                    onClick={() => toggleDelete(u.id)}
                    disabled={isSaving || isDefaultAdmin}
                  >
                    {u.toDelete ? '取消' : '削除'}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DbAdmin;
